import { useEffect, useRef, useCallback, useState, useMemo } from "react";
import "./MapStyle.css";
import mapboxgl from "mapbox-gl";
import { SearchBoxCore, SessionToken } from "@mapbox/search-js-core";
import "mapbox-gl/dist/mapbox-gl.css";
import { useDispatch } from "react-redux";
import {
  deepSearchFunction,
  fetchSingleProgram,
} from "../../../redux/actions/immobilierActions";
import { imageLoader } from "../../../utils/imageLoader";
import MapboxGeocoder from "@mapbox/mapbox-gl-geocoder";
import "@mapbox/mapbox-gl-geocoder/dist/mapbox-gl-geocoder.css";
import { MapboxStyleSwitcherControl } from "mapbox-gl-style-switcher";
import "mapbox-gl-style-switcher/styles.css";
import "@mapbox/mapbox-gl-directions/dist/mapbox-gl-directions.css";
import POIMarker from "./POIMarker";
import { CgSpinner } from "react-icons/cg";
import mainAxios from "../../../redux/api/mainAxios";
import {
  Airport,
  Banques,
  Cart,
  Hospital,
  Restaurants,
  School,
} from "./MapSVGS";
const MapboxDirections = require("@mapbox/mapbox-gl-directions/dist/mapbox-gl-directions");

// Cluster related ids/constants (avoid typos & recreate)
const CLUSTER_SOURCE_ID = "programs";
const CLUSTER_LAYER_ID = "clusters";
const CLUSTER_COUNT_LAYER_ID = "cluster-count";
const UNCLUSTERED_LAYER_ID = "unclustered-point";

mapboxgl.accessToken = process.env.REACT_APP_MAPBOX_ACCESS_TOKEN;

const MapView = ({
  locations,
  zoom = 10,
  searchOnMap = false,
  setSelectedProgram = () => {},
  selectedProgram = null,
  filterInput,
  ongletType,
  ongletId,
  ongletName,
  query,
}) => {
  const mapContainerRef = useRef(null);
  const mapRef = useRef(null);
  const locationsRef = useRef(locations);
  const markersRef = useRef({});
  const hasMounted = useRef(false);
  const [searchCategory, setSearchCategory] = useState(null);
  const [excludedCategory, setExcludedCategory] = useState(null);
  const searchRef = useRef();
  const sessionTokenRef = useRef(null);
  const programCacheRef = useRef(new Map());
  const dispatch = useDispatch();
  const [loading, setLoading] = useState(false);
  const [showDirectionControl, setShowDirectionControl] = useState(false);
  const [searchResults, setSearchResults] = useState([]);
  const [mapBounds, setMapBounds] = useState();
  const [searchBounds, setSearchBounds] = useState();
  const [showSearchAreaButton, setShowSearchAreaButton] = useState(false);
  const [locationsGeoJSON, setLocationsGeoJSON] = useState(null);
  const [initialLocateDone, setInitialLocateDone] = useState(false);

  useEffect(() => {
    locationsRef.current = locations;
  }, [locations]);

  // Category buttons configuration
  const categoryButtons = [
    {
      icon: <School />,
      label: "Écoles",
      value: "high_school,elementary_school,college,kindergarten",
      excluded:
        "tutor,dormitory,community_college,university_book_store,university_laboratory",
    },
    {
      icon: <Hospital />,
      label: "Hôpitaux",
      value: "hospital",
      excluded: "medical_laboratory,pharmacy,doctors_office,dentist",
    },
    {
      icon: <Airport />,
      label: "Aéroports",
      value: "airport_terminal",
      excluded: "",
    },
    {
      icon: <Cart />,
      label: "Supermarchés",
      value: "supermarket",
      excluded: "",
    },
    {
      icon: <Restaurants />,
      label: "Restaurants",
      value: "restaurant",
      excluded: "",
    },
    { icon: <Banques />, label: "Banques", value: "bank", excluded: "" },
  ];

  // Function to convert locations to GeoJSON format
  const convertLocationsToGeoJSON = useCallback((locationData) => {
    if (!locationData || locationData.length === 0) return null;

    return {
      type: "FeatureCollection",
      features: locationData.map((location) => ({
        type: "Feature",
        geometry: {
          type: "Point",
          coordinates: [
            parseFloat(location.longitude),
            parseFloat(location.latitude),
          ],
        },
      })),
    };
  }, []);

  // Convert locations to GeoJSON when they change
  useEffect(() => {
    if (locations && locations.length > 0) {
      const geoJSON = convertLocationsToGeoJSON(locations);
      setLocationsGeoJSON(geoJSON);
    }
  }, [locations, convertLocationsToGeoJSON]);

  useEffect(() => {
    function boundsChanged(boundsA, boundsB) {
      if (!boundsA || !boundsB) return false;
      return JSON.stringify(boundsA) !== JSON.stringify(boundsB);
    }

    if (searchCategory && boundsChanged(mapBounds, searchBounds)) {
      setShowSearchAreaButton(true);
    } else {
      setShowSearchAreaButton(false);
    }
  }, [mapBounds, searchCategory, searchBounds]);

  const geocoderControl = useMemo(
    () =>
      new MapboxGeocoder({
        accessToken: mapboxgl.accessToken,
        mapboxgl: mapboxgl,
        placeholder: "Rechercher un lieu",
        clearAndBlurOnEsc: true,
        enableGeolocation: true,
        marker: true,
        language: "fr",
      }),
    []
  );
  const directionsControl = useMemo(() => {
    // Create the directions control with French language
    const directions = new MapboxDirections({
      accessToken: mapboxgl.accessToken,
      unit: "metric",
      profile: "mapbox/driving",
      language: "fr",
      placeholderOrigin: "Point de départ",
      placeholderDestination: "Destination",
      interactive: false,
      controls: {
        inputs: true,
        instructions: true,
        profileSwitcher: true,
      },
    });

    // Add collapse/expand functionality and duration display after the directions control is added to the map
    directions.on("route", (event) => {
      // Wait for DOM to be updated with route instructions
      setTimeout(() => {
        const instructionsContainer = document.querySelector(
          ".mapbox-directions-instructions"
        );

        if (instructionsContainer) {
          // Add collapse button if it doesn't exist
          if (!document.querySelector(".directions-collapse-btn")) {
            // Create collapse button
            const collapseBtn = document.createElement("div");
            collapseBtn.className = "directions-collapse-btn";
            collapseBtn.title = "Réduire/Agrandir les instructions";

            // Add click handler to toggle collapsed state
            collapseBtn.addEventListener("click", () => {
              instructionsContainer.classList.toggle("collapsed");
              collapseBtn.classList.toggle("collapsed");
            });

            // Add the button to the instructions container
            instructionsContainer.appendChild(collapseBtn);
          }

          // Add or update duration display
          if (event.route && event.route[0]) {
            const route = event.route[0];
            const durationInSeconds = route.duration;
            const distanceInMeters = route.distance;

            // Convert duration to hours and minutes
            const hours = Math.floor(durationInSeconds / 3600);
            const minutes = Math.round((durationInSeconds % 3600) / 60);

            // Convert distance to km
            const distanceInKm = (distanceInMeters / 1000).toFixed(1);

            // Format duration string
            let durationText = "";
            if (hours > 0) {
              durationText = `${hours}h ${minutes}min`;
            } else {
              durationText = `${minutes}min`;
            }

            // Remove existing duration display if any
            const existingDuration = document.querySelector(
              ".directions-duration-display"
            );
            if (existingDuration) {
              existingDuration.remove();
            }

            // Create duration display element
            const durationDisplay = document.createElement("div");
            durationDisplay.className = "directions-duration-display";
            durationDisplay.innerHTML = `
              <div style="background: linear-gradient(to right, #5a9999, #3f7ab2); color: white; padding: 12px 16px; border-radius: 8px; margin: 10px 0; font-size: 14px; font-weight: 500; display: flex; justify-content: space-between; align-items: center; box-shadow: 0 2px 4px rgba(0,0,0,0.1);">
                <div style="display: flex; align-items: center; gap: 8px;">
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                    <circle cx="12" cy="12" r="10"></circle>
                    <polyline points="12 6 12 12 16 14"></polyline>
                  </svg>
                  <span>Durée estimée: <strong>${durationText}</strong></span>
                </div>
              </div>
            `;

            // Insert duration display at the beginning of instructions container
            instructionsContainer.insertBefore(
              durationDisplay,
              instructionsContainer.firstChild
            );

            // Make the default route summary h1 empty
            const routeSummaryH1 = document.querySelector(
              ".mapbox-directions-route-summary h1"
            );
            if (routeSummaryH1) {
              // Set to empty string to keep the element but show nothing
              routeSummaryH1.textContent = "";
            }
          }
        }
      }, 500); // Increased timeout to ensure DOM is fully updated
    });

    return directions;
  }, []);

  // Function to handle itinerary button click
  const handleItineraryClick = useCallback(
    (program, poiCoordinates) => {
      if (!directionsControl || !poiCoordinates) return;

      // Use the provided program or fall back to the currently selected program
      const originProgram = program || selectedProgram;

      if (originProgram) {
        // Set the program location as origin
        directionsControl.setOrigin([
          originProgram.longitude,
          originProgram.latitude,
        ]);

        // Force populate the origin input field with coordinates
        setTimeout(() => {
          const originInput = document.querySelector(
            '.mapbox-directions-origin input[type="text"]'
          );
          if (originInput) {
            // Display just the coordinates
            const coordinates = `${originProgram.latitude}, ${originProgram.longitude}`;

            // Set the input value
            originInput.value = coordinates;

            // Trigger input event to ensure Mapbox recognizes the change
            const inputEvent = new Event("input", { bubbles: true });
            originInput.dispatchEvent(inputEvent);

            // Also trigger change event
            const changeEvent = new Event("change", { bubbles: true });
            originInput.dispatchEvent(changeEvent);
          }
        }, 100); // Small delay to ensure DOM is ready
      }

      // Set the POI coordinates as destination
      directionsControl.setDestination(poiCoordinates);

      // Force populate the destination input field with coordinates
      setTimeout(() => {
        const destinationInput = document.querySelector(
          '.mapbox-directions-destination input[type="text"]'
        );
        if (destinationInput && poiCoordinates) {
          // Display just the coordinates [longitude, latitude] -> "latitude, longitude"
          const coordinates = `${poiCoordinates[1]}, ${poiCoordinates[0]}`;

          // Set the input value
          destinationInput.value = coordinates;

          // Trigger input event to ensure Mapbox recognizes the change
          const inputEvent = new Event("input", { bubbles: true });
          destinationInput.dispatchEvent(inputEvent);

          // Also trigger change event
          const changeEvent = new Event("change", { bubbles: true });
          destinationInput.dispatchEvent(changeEvent);
        }
      }, 150); // Slightly longer delay to ensure destination is set after origin
    },
    [directionsControl, selectedProgram]
  );

  // Function to update directions origin based on selected program
  const updateDirectionsOrigin = useCallback(() => {
    if (!mapRef.current || !directionsControl || !selectedProgram) return;

    // Set the selected program's position as the origin for directions
    directionsControl.setOrigin([
      selectedProgram.longitude,
      selectedProgram.latitude,
    ]);

    // Force populate the origin input field with coordinates
    setTimeout(() => {
      const originInput = document.querySelector(
        '.mapbox-directions-origin input[type="text"]'
      );
      if (originInput && selectedProgram) {
        // Display just the coordinates
        const coordinates = `${selectedProgram.latitude}, ${selectedProgram.longitude}`;

        // Set the input value
        originInput.value = coordinates;

        // Trigger input event to ensure Mapbox recognizes the change
        const inputEvent = new Event("input", { bubbles: true });
        originInput.dispatchEvent(inputEvent);

        // Also trigger change event
        const changeEvent = new Event("change", { bubbles: true });
        originInput.dispatchEvent(changeEvent);
      }
    }, 100); // Small delay to ensure DOM is ready
  }, [directionsControl, selectedProgram]);

  // Function to handle reverse button click and swap displayed coordinates
  const handleReverseButtonClick = useCallback(() => {
    setTimeout(() => {
      const originInput = document.querySelector(
        '.mapbox-directions-origin input[type="text"]'
      );
      const destinationInput = document.querySelector(
        '.mapbox-directions-destination input[type="text"]'
      );

      if (originInput && destinationInput) {
        // Get current values
        const originValue = originInput.value;
        const destinationValue = destinationInput.value;

        // Swap the values
        originInput.value = destinationValue;
        destinationInput.value = originValue;

        // Trigger events for both inputs
        [originInput, destinationInput].forEach((input) => {
          const inputEvent = new Event("input", { bubbles: true });
          input.dispatchEvent(inputEvent);

          const changeEvent = new Event("change", { bubbles: true });
          input.dispatchEvent(changeEvent);
        });
      }
    }, 100); // Small delay to ensure the reverse operation is complete
  }, []);

  // Effect to add event listener for reverse button
  useEffect(() => {
    const addReverseButtonListener = () => {
      const reverseButton = document.querySelector(
        ".directions-icon.directions-icon-reverse.directions-reverse.js-reverse-inputs"
      );
      if (reverseButton && !reverseButton.hasAttribute("data-listener-added")) {
        reverseButton.addEventListener("click", handleReverseButtonClick);
        reverseButton.setAttribute("data-listener-added", "true");
      }
    };

    // Try to add listener immediately
    addReverseButtonListener();

    // Also add listener when directions control becomes visible
    const observer = new MutationObserver(() => {
      addReverseButtonListener();
    });

    // Observe the document for changes
    observer.observe(document.body, { childList: true, subtree: true });

    return () => {
      observer.disconnect();
      // Clean up listener if it exists
      const reverseButton = document.querySelector(
        ".directions-icon.directions-icon-reverse.directions-reverse.js-reverse-inputs"
      );
      if (reverseButton) {
        reverseButton.removeEventListener("click", handleReverseButtonClick);
        reverseButton.removeAttribute("data-listener-added");
      }
    };
  }, [handleReverseButtonClick]);

  // Optimized map initialization
  const initializeMap = useCallback(
    (container, center, zoom) => {
      try {
        const map = new mapboxgl.Map({
          container,
          style: "mapbox://styles/mapbox/streets-v8",
          center,
          zoom,
          config: {
            basemap: {
              showPointOfInterestLabels: false,
            },
          },
        });

        map.addControl(new mapboxgl.FullscreenControl());
        map.addControl(new mapboxgl.NavigationControl(), "bottom-left");
        map.addControl(geocoderControl, "bottom-right");
        map.addControl(new MapboxStyleSwitcherControl(), "bottom-left");
        map.addControl(directionsControl, "top-right");

        // Create and add geolocate control
        const geolocateControl = new mapboxgl.GeolocateControl({
          positionOptions: {
            enableHighAccuracy: true,
          },
          trackUserLocation: true,
          showUserHeading: true,
          showUserLocation: true,
          fitBoundsOptions: {
            maxZoom: 15,
          },
        });

        map.addControl(geolocateControl, "bottom-right");

        // Handle map load event more efficiently
        map.once("load", () => {
          setMapBounds(map.getBounds().toArray());

          if (map.getLayer("waterway-label")) {
            map.moveLayer("waterway-label");
          }

          map.resize();

          // AUTO-TRIGGER GEOLOCATION HERE
          setTimeout(() => {
            try {
              geolocateControl.trigger();
            } catch (error) {
              console.log("Geolocation trigger failed:", error);
            }
          }, 1000); // Small delay to ensure map is fully loaded
        });

        // Listen for geolocation events
        geolocateControl.once("geolocate", (e) => {
          if (initialLocateDone) return;
          const userCoords = [e.coords.longitude, e.coords.latitude];
          // Zoom to user location when found
          map.flyTo({
            center: userCoords,
            zoom: 10,
            duration: 1500,
            essential: true,
          });
          setInitialLocateDone(true);
        });

        // // Handle geolocation errors
        geolocateControl.on("error", (error) => {
          console.log("Geolocation error:", error);
          // You can show a fallback message or use default location
        });

        // Update map bounds when moving
        map.on("moveend", () => {
          const newBounds = map.getBounds().toArray();
          // Only update bounds if they actually changed
          setMapBounds((prevBounds) => {
            if (
              !prevBounds ||
              Math.abs(prevBounds[0][0] - newBounds[0][0]) > 0.001 ||
              Math.abs(prevBounds[0][1] - newBounds[0][1]) > 0.001 ||
              Math.abs(prevBounds[1][0] - newBounds[1][0]) > 0.001 ||
              Math.abs(prevBounds[1][1] - newBounds[1][1]) > 0.001
            ) {
              return newBounds;
            }
            return prevBounds;
          });
        });

        // Handle user-initiated map interactions (drag, pan) with debounce
        let userMoveTimeout;
        map.on("dragend", () => {
          clearTimeout(userMoveTimeout);
          userMoveTimeout = setTimeout(() => {
            const currentZoom = map.getZoom();
            toggleMarkersVisibility(currentZoom);
          }, 300);
        });

        // Handle zoom changes
        map.on("zoom", () => {
          const newZoom = map.getZoom();
          // Toggle marker visibility based on zoom level
          toggleMarkersVisibility(newZoom);
        });

        // Handle potential errors
        map.on("error", (e) => {
          console.error("Mapbox error:", e.error);
        });

        // Initialize the search box with a new session token
        searchRef.current = new SearchBoxCore({
          accessToken: mapboxgl.accessToken,
        });
        sessionTokenRef.current = new SessionToken();

        return map;
      } catch (error) {
        console.error("Error initializing map:", error);
        return null;
      }
    },
    [geocoderControl, directionsControl]
  );

  // Function to toggle cluster visibility based on zoom level
  const toggleClusterVisibility = useCallback((zoomLevel) => {
    const map = mapRef.current;
    if (!map) return;

    const shouldShowClusters = zoomLevel < 9;

    // Toggle cluster layers visibility
    const clusterLayers = [
      CLUSTER_LAYER_ID,
      CLUSTER_COUNT_LAYER_ID,
      UNCLUSTERED_LAYER_ID,
      "unclustered-text",
    ];

    clusterLayers.forEach((layerId) => {
      if (map.getLayer(layerId)) {
        map.setLayoutProperty(
          layerId,
          "visibility",
          shouldShowClusters ? "visible" : "none"
        );
      }
    });
  }, []);

  // Function to create/destroy markers based on zoom level and viewport bounds
  const toggleMarkersVisibility = useCallback(
    (zoomLevel) => {
      const shouldShowMarkers = zoomLevel >= 9;
      const map = mapRef.current;
      if (!map || !locationsRef.current) return;

      // Toggle cluster visibility based on marker visibility
      toggleClusterVisibility(zoomLevel);

      if (shouldShowMarkers) {
        // Get current map bounds
        const bounds = map.getBounds();
        const sw = bounds.getSouthWest();
        const ne = bounds.getNorthEast();

        // Filter locations to only those within current viewport
        const locationsInViewport = locationsRef.current.filter((location) => {
          return (
            location.longitude >= sw.lng &&
            location.longitude <= ne.lng &&
            location.latitude >= sw.lat &&
            location.latitude <= ne.lat
          );
        });

        // Remove markers that are no longer in viewport
        Object.entries(markersRef.current).forEach(
          ([documentId, { marker, location }]) => {
            const isInViewport = locationsInViewport.some(
              (loc) => loc.documentId === documentId
            );
            if (!isInViewport) {
              // Properly cleanup marker and popup
              const popup = marker.getPopup();
              if (popup) {
                popup.remove();
                // Remove all popup event listeners
                popup.off("open");
                popup.off("close");
              }

              // Remove marker from map
              marker.remove();
              delete markersRef.current[documentId];
            }
          }
        );
        // Create markers for locations in viewport that don't exist yet
        locationsInViewport.forEach((location) => {
          if (!markersRef.current[location.documentId]) {
            createMarkerFunction(location, map);
          }
        });
      } else {
        // Remove all markers when zoomed out
        Object.values(markersRef.current).forEach(({ marker }) => {
          if (marker) {
            const popup = marker.getPopup();
            if (popup) {
              popup.remove();
              // Remove all popup event listeners
              popup.off("open");
              popup.off("close");
            }
            marker.remove();
          }
        });
        markersRef.current = {};
      }
    },
    [locations, toggleClusterVisibility]
  );

  // Helper function to create markers (defined inline to avoid hoisting issues)
  const createMarkerFunction = (location, map) => {
    // Create a popup with offset to position it below the marker
    const popup = new mapboxgl.Popup({
      anchor: "top",
      closeButton: false,
      closeOnClick: true,
    });

    // Function to fetch and display program data in popup
    const fetchAndDisplayProgram = async (documentId) => {
      try {
        // Check if program data is already cached
        if (programCacheRef.current.has(documentId)) {
          const cachedProgram = programCacheRef.current.get(documentId);
          displayProgramData(cachedProgram, documentId);
          return;
        }

        // Show loading state
        popup.setHTML(`
          <div style="max-width: 180px; text-align: center; padding: 10px;">
            <div style="display: flex; align-items: center; justify-content: center; gap: 8px;">
              <div class="spinner" style="width: 16px; height: 16px; border: 2px solid #f3f3f3; border-top: 2px solid #e91e63; border-radius: 50%; animation: spin 1s linear infinite;"></div>
              <span>Chargement...</span>
            </div>
          </div>
          <style>
            @keyframes spin {
              0% { transform: rotate(0deg); }
              100% { transform: rotate(360deg); }
            }
          </style>
        `);

        // Fetch program data using the API directly
        const response = await mainAxios.get(
          `/api/programmes/${documentId}?populate[images][populate][image][fields][0]=url&populate[promoteur][populate][logo][fields][0]=url&populate[lots][populate][images][populate][image][fields][0]=url`
        );
        if (!response) {
          throw new Error("Failed to fetch program data");
        }

        const program = response.data.data;

        // Cache the fetched program data
        programCacheRef.current.set(documentId, program);

        // Display the program data
        displayProgramData(program, documentId);
      } catch (error) {
        console.error("Error fetching program data:", error);
        // Show error state
        popup.setHTML(`
          <div style="max-width: 180px; text-align: center; padding: 10px; color: #e91e63;">
            <div>Erreur lors du chargement</div>
            <div style="font-size: 12px; margin-top: 5px; color: #666;">Veuillez réessayer</div>
          </div>
        `);
      }
    };

    // Helper function to display program data in popup
    const displayProgramData = (program, documentId) => {
      // Helper function to get programme image URL with same priority as ProgramCard
      const getProgrammeImageUrl = (programme) => {
        // Check programme images first
        if (programme.images?.[0]?.image?.[0]?.url) {
          return imageLoader(programme.images[0].image[0].url);
        }

        // Find first lot with an image
        const lotWithImage = programme.lots?.find(
          (lot) => lot.images?.[0]?.image?.[0]?.url
        );
        if (lotWithImage?.images?.[0]?.image?.[0]?.url) {
          return imageLoader(lotWithImage.images[0].image[0].url);
        }

        // Check promoteur logo
        if (programme.promoteur?.logo?.url) {
          return imageLoader(programme.promoteur.logo.url);
        }

        // Final fallback
        return "./assets/imgNotFound.webp";
      };

      const programImage = getProgrammeImageUrl(program);
      const defaultFallbackImage = "./assets/imgNotFound.webp";

      // Display the fetched program data
      popup.setHTML(`
        <div style="width: 100%;">
          <div style="text-align: center; margin-bottom: 5px; font-weight: 600; font-size: 14px; text-overflow: ellipsis; white-space: nowrap; overflow: hidden;">${
            program?.name || "Programme"
          }</div>
          <img
            src="${programImage}"
            alt="Program"
            style="width: 100%; height: 160px; object-fit: contain; border-radius: 10px; margin-bottom: 10px"
            onerror="this.src='${defaultFallbackImage}'"
          />
          <div style="display: flex; flex-direction: column; gap: 8px">
            <button data-program-id="${documentId}" style="width: 100%; margin-top: 10px; border: none; background-color: #e91e63; cursor: pointer; padding: 8px 4px; border-radius: 4px; font-size: 14px; color: white; font-weight: 500;">Voir plus</button>
          </div>
        </div>
      `);

      // Add click event listener to the "Voir plus" button
      const popupElement = popup.getElement();
      if (popupElement) {
        const programButton = popupElement.querySelector(
          "button[data-program-id]"
        );
        if (programButton) {
          programButton.addEventListener("click", (e) => {
            e.stopPropagation();
            const programId = programButton.getAttribute("data-program-id");
            // Dispatch Redux action to open program in new onglet
            dispatch(fetchSingleProgram(programId, true));
          });
        }
      }
    };

    popup.on("open", () => {
      // Fetch and display program data when popup opens
      fetchAndDisplayProgram(location.documentId);
      setSelectedProgram(location);
    });

    popup.on("close", () => {
      // Clear selection when popup closes
      // setSelectedProgram(null);
    });

    const el = document.createElement("div");
    el.className = "marker";

    // Create a marker with default Mapbox marker
    const marker = new mapboxgl.Marker(el)
      .setLngLat([location.longitude, location.latitude])
      .setPopup(popup)
      .addTo(map);

    // Add click handler to marker with better event handling
    marker.getElement().addEventListener("click", (e) => {
      e.stopPropagation();
      e.preventDefault();

      // Ensure the marker still exists in our reference
      if (!markersRef.current[location.documentId]) {
        return;
      }

      // Clear current directions completely when selecting a new program
      if (directionsControl) {
        directionsControl.removeRoutes();
      }

      // Close all other popups first
      Object.values(markersRef.current).forEach(({ marker: m }) => {
        if (m && m !== marker) {
          const popup = m.getPopup();
          if (popup && popup.isOpen()) {
            popup.remove();
          }
        }
        if (m && m.getElement()) {
          m.getElement().style.filter = "none";
        }
      });

      // Ensure popup exists and is not already open
      if (popup && !popup.isOpen()) {
        // Open this marker's popup
        popup.addTo(map);
      }

      // Highlight this marker
      if (marker.getElement()) {
        marker.getElement().style.filter =
          "drop-shadow(0 0 8px rgba(255, 56, 92, 0.8))";
      }
    });

    // Store marker reference for later updates
    markersRef.current[location.documentId] = {
      marker,
      location,
    };

    return marker;
  };

  const performCategorySearch = useCallback(async () => {
    if (!searchCategory || !mapBounds || !searchRef.current) return;

    try {
      if (!sessionTokenRef.current) {
        sessionTokenRef.current = new SessionToken();
      }

      // Format bbox correctly: [west, south, east, north]
      const formattedBbox = [
        mapBounds[0][0], // west
        mapBounds[0][1], // south
        mapBounds[1][0], // east
        mapBounds[1][1], // north
      ];

      const response = await searchRef.current.category(searchCategory, {
        bbox: formattedBbox,
        excluded: excludedCategory,
        language: "fr",
        country: "fr",
        limit: 25,
      });

      if (response && response.features) {
        setSearchResults(response.features);
        setSearchBounds(mapBounds);
      }
    } catch (error) {
      console.error("Error performing category search:", error);
    }
  }, [searchCategory, mapBounds]);

  // when the searchCategory changes, perform a category search
  useEffect(() => {
    if (searchCategory) {
      performCategorySearch();
    }
  }, [searchCategory]);

  const handleCategoryChange = useCallback(
    (category, excluded) => {
      if (category === searchCategory) {
        setSearchCategory(null);
        setExcludedCategory(null);
        setSearchResults([]);
      } else {
        setSearchCategory(category);
        setExcludedCategory(excluded);
      }
    },
    [searchCategory]
  );

  // Memoised function that adds cluster source & layers only once
  const addClusterLayers = useCallback((mapInstance, geojson) => {
    if (!mapInstance || mapInstance.getSource(CLUSTER_SOURCE_ID)) return;

    mapInstance.addSource(CLUSTER_SOURCE_ID, {
      type: "geojson",
      data: geojson,
      cluster: true,
      clusterMaxZoom: 12,
      clusterRadius: 50,
    });

    mapInstance.addLayer({
      id: CLUSTER_LAYER_ID,
      type: "circle",
      source: CLUSTER_SOURCE_ID,
      filter: ["has", "point_count"],
      paint: {
        "circle-color": [
          "step",
          ["get", "point_count"],
          "#51bbd6",
          100,
          "#f1f075",
          750,
          "#f28cb1",
        ],
        "circle-radius": ["step", ["get", "point_count"], 20, 100, 30, 750, 40],
      },
    });

    mapInstance.addLayer({
      id: CLUSTER_COUNT_LAYER_ID,
      type: "symbol",
      source: CLUSTER_SOURCE_ID,
      filter: ["has", "point_count"],
      layout: {
        "text-field": ["get", "point_count_abbreviated"],
        "text-font": ["DIN Offc Pro Medium", "Arial Unicode MS Bold"],
        "text-size": 12,
      },
    });

    mapInstance.addLayer({
      id: UNCLUSTERED_LAYER_ID,
      type: "circle",
      source: CLUSTER_SOURCE_ID,
      filter: ["!", ["has", "point_count"]],
      paint: {
        "circle-color": "#11b4da",
        "circle-radius": 20,
      },
    });

    mapInstance.addLayer({
      id: "unclustered-text",
      type: "symbol",
      source: CLUSTER_SOURCE_ID,
      filter: ["!", ["has", "point_count"]],
      layout: {
        "text-field": "1",
        "text-font": ["DIN Offc Pro Medium", "Arial Unicode MS Bold"],
        "text-size": 12,
      },
      paint: {
        "text-color": "#000000",
      },
    });

    // click to zoom on cluster (listener added once)
    const clickHandler = (e) => {
      const features = mapInstance.queryRenderedFeatures(e.point, {
        layers: [CLUSTER_LAYER_ID],
      });
      if (!features.length) return;
      const clusterId = features[0].properties.cluster_id;
      mapInstance
        .getSource(CLUSTER_SOURCE_ID)
        .getClusterExpansionZoom(clusterId, (err, zoom) => {
          if (err) return;
          mapInstance.easeTo({
            center: features[0].geometry.coordinates,
            zoom,
          });
        });
    };
    mapInstance.on("click", CLUSTER_LAYER_ID, clickHandler);

    // store handler for removal on unmount
    clusterClickHandlerRef.current = clickHandler;
  }, []);

  const clusterClickHandlerRef = useRef(null);

  // Initialize map - reinitialize every time component mounts
  useEffect(() => {
    // Always cleanup existing map first
    if (mapRef.current) {
      mapRef.current.remove();
      mapRef.current = null;
      markersRef.current = {};
    }

    // Initialize new map if container and locations are available
    if (mapContainerRef.current && locations) {
      mapRef.current = initializeMap(
        mapContainerRef.current,
        [2.3488, 48.8534],
        zoom
      );

      // Don't create markers initially - they will be created when zooming in
      // Initial marker visibility check
      setTimeout(() => {
        if (mapRef.current) {
          toggleMarkersVisibility(mapRef.current.getZoom());
        }
      }, 100);

      // Add a MutationObserver to watch for the directions instructions container
      const observer = new MutationObserver((mutations) => {
        mutations.forEach((mutation) => {
          if (mutation.addedNodes.length) {
            const instructionsContainer = document.querySelector(
              ".mapbox-directions-instructions"
            );
            if (
              instructionsContainer &&
              !instructionsContainer.querySelector(".directions-collapse-btn")
            ) {
              // Create collapse button
              const collapseBtn = document.createElement("div");
              collapseBtn.className = "directions-collapse-btn";
              collapseBtn.title = "Réduire/Agrandir les instructions";

              // Add click handler to toggle collapsed state
              collapseBtn.addEventListener("click", () => {
                instructionsContainer.classList.toggle("collapsed");
                collapseBtn.classList.toggle("collapsed");
              });

              // Add the button to the instructions container
              instructionsContainer.appendChild(collapseBtn);
            }
          }
        });
      });

      // Start observing the document body for changes
      observer.observe(document.body, { childList: true, subtree: true });
    }

    return () => {
      if (mapRef.current) {
        mapRef.current.remove();
        mapRef.current = null;
        markersRef.current = {};
      }
    };
  }, [initializeMap]);

  // flyTo effect when a new program is selected
  useEffect(() => {
    if (
      selectedProgram &&
      selectedProgram.longitude &&
      selectedProgram.latitude
    ) {
      if (mapRef.current) {
        try {
          // Your flyTo code with additional logging
          const coordinates = [
            selectedProgram.longitude,
            selectedProgram.latitude,
          ];
          mapRef.current.flyTo({
            center: coordinates,
            zoom: 14,
            duration: 1200,
            essential: true,
          });
          updateDirectionsOrigin();
          // Wait for flyTo animation to complete using setTimeout
          setTimeout(() => {
            // Find the marker for the selected program
            const markerData = markersRef.current[selectedProgram.documentId];

            if (markerData && markerData.marker) {
              const { marker } = markerData;

              // Close all other popups first
              Object.values(markersRef.current).forEach(({ marker: m }) => {
                if (m && m !== marker) {
                  const popup = m.getPopup();
                  if (popup && popup.isOpen()) {
                    popup.remove();
                  }
                }
                if (m && m.getElement()) {
                  m.getElement().style.filter = "none";
                }
              });

              // Open the selected marker's popup
              const popup = marker.getPopup();
              if (popup && !popup.isOpen()) {
                popup.addTo(mapRef.current);

                // Highlight this marker
                if (marker.getElement()) {
                  marker.getElement().style.filter =
                    "drop-shadow(0 0 8px rgba(255, 56, 92, 0.8))";
                }
              }
            }
          }, 1300); // Slightly longer than flyTo duration (1200ms) to ensure completion
        } catch (error) {
          console.error("Error in flyTo:", error);
        }
      }
    }
  }, [selectedProgram, updateDirectionsOrigin]);

  // EFFECT to create / update clusters whenever geojson changes
  useEffect(() => {
    const map = mapRef.current;
    if (!map || !locationsGeoJSON) return;

    if (map.getSource(CLUSTER_SOURCE_ID)) {
      map.getSource(CLUSTER_SOURCE_ID).setData(locationsGeoJSON);
    } else if (map.loaded()) {
      addClusterLayers(map, locationsGeoJSON);
    } else {
      map.once("load", () => addClusterLayers(map, locationsGeoJSON));
    }
  }, [locationsGeoJSON, addClusterLayers]);

  // cleanup cluster click listener on unmount
  useEffect(
    () => () => {
      const map = mapRef.current;
      if (map && clusterClickHandlerRef.current) {
        map.off("click", CLUSTER_LAYER_ID, clusterClickHandlerRef.current);
      }
    },
    []
  );

  //EFFECT TO FETCH PROGRAM BASED ON MAP ZOOM CHANGES ONLY
  const prevMapBoundsRef = useRef(null);
  useEffect(() => {
    if (!mapRef.current || !searchOnMap || !mapBounds) return;
    if (!hasMounted.current) {
      hasMounted.current = true;
      return;
    }

    // Simple bounds change check
    const [[minLng, minLat], [maxLng, maxLat]] = mapBounds;

    // Skip if previous bounds exist and haven't changed significantly
    if (prevMapBoundsRef.current) {
      const [[prevMinLng, prevMinLat], [prevMaxLng, prevMaxLat]] =
        prevMapBoundsRef.current;

      // Calculate center points
      const centerLng = (minLng + maxLng) / 2;
      const centerLat = (minLat + maxLat) / 2;
      const prevCenterLng = (prevMinLng + prevMaxLng) / 2;
      const prevCenterLat = (prevMinLat + prevMaxLat) / 2;

      // If center hasn't moved much, skip the search
      const movedSignificantly =
        Math.abs(centerLng - prevCenterLng) > 0.01 ||
        Math.abs(centerLat - prevCenterLat) > 0.01;

      if (!movedSignificantly) {
        return; // Skip search if center hasn't moved significantly
      }
    }

    setLoading(true);

    const timeout = setTimeout(() => {
      // Update previous bounds reference
      prevMapBoundsRef.current = mapBounds;

      dispatch(
        deepSearchFunction(
          { ...query, search: filterInput, minLat, maxLat, minLng, maxLng },
          ongletType,
          ongletId,
          ongletName,
          true
        )
      );

      setLoading(false);
    }, 700);

    return () => clearTimeout(timeout);
  }, [searchOnMap, mapBounds, dispatch]);

  const toggleDirectionControl = () => {
    setShowDirectionControl(!showDirectionControl);
  };

  // Effect to update directions control visibility based on state
  useEffect(() => {
    if (!mapRef.current) return;

    const directionsContainer = mapRef.current
      .getContainer()
      .querySelector(".mapboxgl-ctrl-directions");
    const directionButton = document.querySelector(".direction-button");
    if (directionsContainer) {
      if (showDirectionControl) {
        directionsContainer.classList.add("directions-visible");
        directionButton?.classList.add("active");
      } else {
        directionsContainer.classList.remove("directions-visible");
        directionButton?.classList.remove("active");
      }
    }
  }, [showDirectionControl]);

  return (
    <div className="map-wrapper">
      {/* Empty map container as required by Mapbox GL JS */}
      <div className="mapView radius5" ref={mapContainerRef}></div>

      {/* Overlay elements positioned absolutely over the map */}
      {loading && (
        <div className="map-loader" id="map-loader">
          <CgSpinner className="map-loader-icon" />
        </div>
      )}

      {/* Category buttons positioned over the map */}
      <div className="category-button-container">
        {categoryButtons.map(({ icon, label, value, excluded }) => (
          <button
            key={value}
            onClick={() => handleCategoryChange(value, excluded)}
            className={`category-button ${
              searchCategory === value ? "active" : ""
            }`}
            title={label}
          >
            <span className="category-icon">{icon}</span>
            <span className="category-label">{label}</span>
          </button>
        ))}
      </div>

      {/* Show "search this area" button */}
      {showSearchAreaButton && (
        <button onClick={performCategorySearch} className="search-area-button">
          Rechercher dans cette zone
        </button>
      )}
      <button className="direction-button" onClick={toggleDirectionControl}>
        <img src="/icons/direction.svg" alt="" height={29} width={29} />
      </button>
      {/* Render POI markers for search results */}
      {mapRef.current &&
        searchResults.map((feature) => (
          <POIMarker
            key={`${
              feature.id || feature.properties.name
            }-${feature.geometry.coordinates.join(",")}`}
            map={mapRef.current}
            feature={feature}
            category={searchCategory}
            locations={locations}
            onItineraryClick={handleItineraryClick}
          />
        ))}
    </div>
  );
};

export default MapView;
